import {
	Box,
	Button,
	Collapse,
	List,
	ListItem,
	ListSubheader,
	styled

} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { NavLink as RouterLink, useLocation } from "react-router-dom";

// Group Subheader Icons
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ReportIcon from '@mui/icons-material/Report';
// Parent Category TwoTone Icons
import CategoryTwoToneIcon from "@mui/icons-material/CategoryTwoTone";
import CardGiftcardTwoToneIcon from "@mui/icons-material/CardGiftcardTwoTone";
import DesignServicesTwoToneIcon from "@mui/icons-material/DesignServicesTwoTone";
import EventNoteTwoToneIcon from "@mui/icons-material/EventNoteTwoTone";
import FestivalTwoToneIcon from "@mui/icons-material/FestivalTwoTone";
import GroupsTwoToneIcon from "@mui/icons-material/GroupsTwoTone";
import PeopleAltTwoToneIcon from "@mui/icons-material/PeopleAltTwoTone";
import StorefrontTwoToneIcon from "@mui/icons-material/StorefrontTwoTone";
import SupervisorAccountTwoToneIcon from "@mui/icons-material/SupervisorAccountTwoTone";
import TableChartTwoToneIcon from "@mui/icons-material/TableChartTwoTone";
import SmartToyTwoToneIcon from "@mui/icons-material/SmartToyTwoTone";
import RateReviewTwoToneIcon from "@mui/icons-material/RateReviewTwoTone";

import { useAuthContext } from "src/components/Context/AuthContext/AuthContext";
import { SidebarContext } from "src/components/Context/SidebarContext";

// Styled Components for Professional Enterprise Navigation
const SidebarNavContainer = styled(Box)(
	({ theme }) => `
	padding: ${theme.spacing(0.5, 0.5)};

	.MuiList-root {
		padding: 0;
	}

	.MuiListItem-root {
		padding: 1px 0;
	}
`
);

// Group Header with Icon
const GroupHeader = styled(ListSubheader)<any>(
	({ theme }) => `
	background: transparent;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: ${theme.spacing(2, 1.5, 0.6, 1.5)};
	font-size: 0.8125rem;
	font-weight: 700;
	letter-spacing: 0.3px;
	text-transform: none;
	line-height: 1.2;
	color: #94A3B8;
`
);

// Indented child button for group lists (e.g. Admin Management, Overview, Accounts, Configurations, Profile)
const GroupChildButton = styled(Button)<any>(
	({ theme }) => `
	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: calc(100% - 12px);
	margin: 1px 6px;
	padding: ${theme.spacing(0.6, 1.25, 0.6, 3.25)};
	min-height: 32px;
	border-radius: 7px;
	font-size: 0.8125rem;
	font-weight: 500;
	color: #CBD5E1;
	text-transform: none;
	letter-spacing: 0.1px;
	transition: all 0.15s ease-in-out;

	.parent-icon {
		margin-right: 8px;
		font-size: 16px;
		color: #94A3B8;
		transition: color 0.15s ease;
	}

	&:hover {
		background-color: rgba(255, 255, 255, 0.05);
		color: #FFFFFF;
		padding-left: ${theme.spacing(3.5)};

		.parent-icon {
			color: #4ADE80;
		}
	}

	&.active {
		background: linear-gradient(90deg, rgba(12, 141, 45, 0.22) 0%, rgba(12, 141, 45, 0.04) 100%);
		color: #4ADE80;
		font-weight: 600;
		border-left: 2.5px solid #10B981;
		padding-left: calc(${theme.spacing(3.25)} - 2.5px);

		.parent-icon {
			color: #34D399;
		}
	}
`
);

// Parent Collapsible Folder Button (Clean & Minimalist without boxy background/border)
const ParentFolderButton = styled(Button)<any>(
	({ theme }) => `
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: calc(100% - 12px);
	margin: 1px 6px;
	padding: ${theme.spacing(0.6, 1.25, 0.6, 3)};
	min-height: 32px;
	border-radius: 7px;
	background: transparent;
	border: none;
	font-size: 0.8125rem;
	font-weight: 500;
	color: #CBD5E1;
	text-transform: none;
	letter-spacing: 0.1px;
	transition: all 0.15s ease-in-out;

	.left-content {
		display: flex;
		align-items: center;
		gap: 9px;
	}

	.parent-icon {
		font-size: 16px;
		color: #94A3B8;
		transition: color 0.15s ease;
	}

	.expand-chevron {
		font-size: 16px;
		color: rgba(255, 255, 255, 0.4);
		transition: transform 0.2s ease, color 0.2s ease;
	}

	&:hover {
		background-color: rgba(255, 255, 255, 0.05);
		color: #FFFFFF;
		padding-left: ${theme.spacing(3.25)};

		.parent-icon {
			color: #4ADE80;
		}
		.expand-chevron {
			color: #4ADE80;
		}
	}

	&.is-open {
		background: transparent;
		color: #FFFFFF;

		.parent-icon {
			color: #34D399;
		}
		.expand-chevron {
			transform: rotate(180deg);
			color: #4ADE80;
		}
	}
`
);

// Child Tree Submenu Box (Seamless Inset Container with Branch Lines)
const SubmenuTreeBox = styled(Box)(
	() => `
	position: relative;
	background: transparent;
	border: none;
	border-radius: 7px;
	margin: 0 6px 3px 6px;
	padding: 2px 6px 2px 34px;

	/* Continuous Vertical Trunk Line */
	&::before {
		content: "";
		position: absolute;
		left: 28px;
		top: 6px;
		bottom: 8px;
		width: 1.5px;
		background: rgba(255, 255, 255, 0.12);
		border-radius: 1px;
	}
`
);

// Child Submenu Item Button with L-Branch Connector & Glowing Node
const NavSubItemButton = styled(Button)<any>(
	({ theme }) => `
	position: relative;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
	margin: 1px 0;
	padding: ${theme.spacing(0.55, 1.25)};
	min-height: 29px;
	border-radius: 5px;
	font-size: 0.785rem;
	font-weight: 500;
	color: #94A3B8;
	text-transform: none;
	letter-spacing: 0.1px;
	transition: all 0.15s ease-in-out;

	/* Horizontal L-Connector from vertical trunk */
	&::before {
		content: "";
		position: absolute;
		left: -14px;
		top: 50%;
		width: 11px;
		height: 12px;
		border-bottom: 1.5px solid rgba(255, 255, 255, 0.14);
		border-left: 1.5px solid rgba(255, 255, 255, 0.14);
		border-bottom-left-radius: 4px;
		transform: translateY(-50%);
		transition: border-color 0.16s ease;
	}

	/* Glowing Connector Node Dot */
	&::after {
		content: "";
		position: absolute;
		left: -4px;
		top: 50%;
		width: 4px;
		height: 4px;
		border-radius: 50%;
		background-color: rgba(255, 255, 255, 0.25);
		transform: translateY(-50%);
		transition: all 0.16s ease;
	}

	&:hover {
		background-color: rgba(255, 255, 255, 0.06);
		color: #FFFFFF;
		padding-left: ${theme.spacing(1.5)};

		&::before {
			border-color: #4ADE80;
		}
		&::after {
			background-color: #4ADE80;
			box-shadow: 0 0 6px rgba(74, 222, 128, 0.8);
		}
	}

	&.active {
		background: rgba(12, 141, 45, 0.2);
		color: #4ADE80;
		font-weight: 600;
		border-left: 2.5px solid #10B981;
		padding-left: calc(${theme.spacing(1.25)} - 2.5px);

		&::before {
			border-color: #10B981;
		}
		&::after {
			background-color: #10B981;
			box-shadow: 0 0 8px rgba(16, 185, 129, 0.9);
		}
	}
`
);

function SidebarMenu() {
	const { closeSidebar } = useContext(SidebarContext);
	const { user } = useAuthContext();
	const location = useLocation();

	const [expanded, setExpanded] = useState<string | false>(() => {
		const path = window.location.pathname;
		if (path.startsWith("/visitor")) return "panel6";
		if (path.startsWith("/fair-event")) return "panel10";
		if (path.startsWith("/b2b-session") || path.startsWith("/b2b")) return "panel-b2b-session";
		if (path.startsWith("/conference&whatson")) return "panel15";
		if (path.startsWith("/raffle-draw")) return "panel11";
		if (path.startsWith("/guest-speaker")) return "panel27";
		if (
			path.startsWith("/service-item") ||
			path.startsWith("/product-categories")
		)
			return "configure-panel1";
		if (
			path.startsWith("/report") ||
			path.includes("-ledger") ||
			path.includes("report")
		)
			return "report-panel1";
		return false;
	});

	useEffect(() => {
		const path = location.pathname;
		if (path.startsWith("/visitor")) {
			setExpanded("panel6");
		} else if (path.startsWith("/fair-event")) {
			setExpanded("panel10");
		} else if (path.startsWith("/b2b-session") || path.startsWith("/b2b")) {
			setExpanded("panel-b2b-session");
		} else if (path.startsWith("/conference&whatson")) {
		} else if (path.startsWith("/raffle-draw")) {
			setExpanded("panel11");
		} else if (path.startsWith("/guest-speaker")) {
			setExpanded("panel27");
		} else if (
			path.startsWith("/service-item") ||
			path.startsWith("/product-categories")
		) {
			setExpanded("configure-panel1");
		} else if (
			path.startsWith("/report") ||
			path.includes("-ledger") ||
			path.includes("report")
		) {
			setExpanded("report-panel1");
		}
	}, [location.pathname]);

	const togglePanel = (panel: string) => {
		setExpanded((prev) => (prev === panel ? false : panel));
	};

	const currentPath = location.pathname;

	return (
		<SidebarNavContainer>
			{/* MAIN / OVERVIEW */}
			<List
				component="div"
				subheader={
					<GroupHeader component="div" disableSticky>
						<SpaceDashboardOutlinedIcon sx={{ fontSize: 15, color: "#34D399" }} />
						<span>Overview</span>
					</GroupHeader>
				}
			>
				<SubmenuTreeBox>
					<ListItem component="div">
						<NavSubItemButton
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/dashboard"
							className={currentPath === "/dashboard" || currentPath === "/" ? "active" : ""}
						>
							<span>Dashboard</span>
						</NavSubItemButton>
					</ListItem>
				</SubmenuTreeBox>
			</List>

			{/* ADMIN (Super Admin Only) */}
			{user?.user_admin_role === "Super Admin" && (
				<List
					component="div"
					subheader={
						<GroupHeader component="div" disableSticky>
							<AdminPanelSettingsOutlinedIcon sx={{ fontSize: 15, color: "#818CF8" }} />
							<span>Admin Management</span>
						</GroupHeader>
					}
				>
					<SubmenuTreeBox>
						<ListItem component="div">
							<NavSubItemButton
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to="/create-admin"
								className={currentPath.startsWith("/create-admin") ? "active" : ""}
							>
								<span>Create Admin</span>
							</NavSubItemButton>
						</ListItem>
						<ListItem component="div">
							<NavSubItemButton
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to="/admin-list"
								className={currentPath.startsWith("/admin-list") ? "active" : ""}
							>
								<span>All Admin</span>
							</NavSubItemButton>
						</ListItem>
						<ListItem component="div">
							<NavSubItemButton
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to="/audit-trail"
								className={currentPath.startsWith("/audit-trail") ? "active" : ""}
							>
								<span>Audit Trail</span>
							</NavSubItemButton>
						</ListItem>
					</SubmenuTreeBox>
				</List>
			)}



			{/* Member (Preserved Commented Section) */}
			{/* <List
				component="div"
				subheader={
					<ListSubheader component="div" disableSticky>
						Member
					</ListSubheader>
				}
			>
				<SubMenuWrapper>
					<List className="accordion-submodule" component="div">
						<Accordion
							className="accordion"
							expanded={expanded === "panel1"}
							onChange={handleChange("panel1")}
						>
							<AccordionSummary
								expandIcon={
									<ExpandMoreIcon className="expand-icon" />
								}
								aria-controls="panel1bh-content"
								id="panel1bh-header"
							>
								<Typography className="accordion-module-name">
									Member List
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ padding: 0 }}>
								<ListItem>
									<Button
										className="submodule-name"
										disableRipple
										component={RouterLink}
										onClick={closeSidebar}
										to="/member-list"
										startIcon={
											<TableChartTwoToneIcon />
										}
									>
										All Member
									</Button>
								</ListItem>
							</AccordionDetails>
						</Accordion>

						<Accordion
							className="accordion"
							expanded={expanded === "panel2"}
							onChange={handleChange("panel2")}
						>
							<AccordionSummary
								expandIcon={
									<ExpandMoreIcon className="expand-icon" />
								}
								aria-controls="panel2bh-content"
								id="panel2bh-header"
							>
								<Typography className="accordion-module-name">
									Member Invoice
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ padding: 0 }}>
								<ListItem component="div">
									<Button
										className="submodule-name"
										disableRipple
										component={RouterLink}
										onClick={closeSidebar}
										to="/invoices/transactions"
										startIcon={
											<TableChartTwoToneIcon />
										}
									>
										All Invoice
									</Button>
								</ListItem>
								<ListItem component="div">
									<Button
										className="submodule-name"
										disableRipple
										component={RouterLink}
										onClick={closeSidebar}
										to="/invoices/create-invoice"
										startIcon={
											<TableChartTwoToneIcon />
										}
									>
										Create Invoice
									</Button>
								</ListItem>
							</AccordionDetails>
						</Accordion>

						<Accordion
							className="accordion"
							expanded={expanded === "panel3"}
							onChange={handleChange("panel3")}
						>
							<AccordionSummary
								expandIcon={
									<ExpandMoreIcon className="expand-icon" />
								}
								aria-controls="panel2bh-content"
								id="panel2bh-header"
							>
								<Typography className="accordion-module-name">
									Service Item
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ padding: 0 }}>
								<List component="div">
									<ListItem component="div">
										<Button
											sx={{
												paddingLeft:
													"18px!important",
											}}
											disableRipple
											component={RouterLink}
											className="submodule-name"
											onClick={closeSidebar}
											to="/service-item/list"
										>
											Service Item
										</Button>
									</ListItem>
									<ListItem component="div">
										<Button
											className="submodule-name"
											sx={{
												paddingLeft:
													"18px!important",
											}}
											disableRipple
											component={RouterLink}
											onClick={closeSidebar}
											to="/service-item/create"
										>
											Create Service Item
										</Button>
									</ListItem>
								</List>
							</AccordionDetails>
						</Accordion>

						<Accordion
							className="accordion"
							expanded={expanded === "panel4"}
							onChange={handleChange("panel4")}
						>
							<AccordionSummary
								expandIcon={
									<ExpandMoreIcon className="expand-icon" />
								}
								aria-controls="panel2bh-content"
								id="panel2bh-header"
							>
								<Typography
									className="accordion-module-name"
									sx={{}}
								>
									Others
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ padding: 0 }}>
								<ListItem component="div">
									<Button
										className="submodule-name"
										disableRipple
										component={RouterLink}
										onClick={closeSidebar}
										to="/member-list/meeting"
										startIcon={
											<TableChartTwoToneIcon />
										}
									>
										Meeting
									</Button>
								</ListItem>
								<ListItem>
									<Button
										className="submodule-name"
										disableRipple
										component={RouterLink}
										onClick={closeSidebar}
										to="/member-list/create-meeting"
										startIcon={
											<TableChartTwoToneIcon />
										}
									>
										Create Meeting
									</Button>
								</ListItem>
								<ListItem component="div">
									<Button
										className="submodule-name"
										disableRipple
										component={RouterLink}
										onClick={closeSidebar}
										to="/event"
										startIcon={
											<TableChartTwoToneIcon />
										}
									>
										Event
									</Button>
								</ListItem>
							</AccordionDetails>
						</Accordion>
					</List>
				</SubMenuWrapper>
			</List> */}

			{/* Training (Preserved Commented Section) */}
			{/* <List
				component='div'
				subheader={
					<ListSubheader component='div' disableSticky>
						Training
					</ListSubheader>
				}
			>
				<SubMenuWrapper>
					<List component='div'>
						<ListItem component='div'>
							<Button
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to='/courses/trainee'
								startIcon={<TableChartTwoToneIcon />}
							>
								Trainee
							</Button>
						</ListItem>
					</List>
				</SubMenuWrapper>
			</List> */}

			{/* FAIR & EXHIBITION MODULE */}
			<List
				component="div"
				subheader={
					<GroupHeader component="div" disableSticky>
						<ConfirmationNumberOutlinedIcon sx={{ fontSize: 15, color: "#34D399" }} />
						<span>Fair &amp; Exhibition</span>
					</GroupHeader>
				}
			>
				{/* Exhibitor List */}
				<ListItem component="div">
					<GroupChildButton
						disableRipple
						component={RouterLink}
						onClick={closeSidebar}
						to="/exhibitor/list"
						className={
							currentPath.startsWith("/exhibitor") ? "active" : ""
						}
					>
						<StorefrontTwoToneIcon className="parent-icon" />
						<span>Exhibitor List</span>
					</GroupChildButton>
				</ListItem>

				{/* Visitor Parent */}
				<Box>
					<ParentFolderButton
						disableRipple
						onClick={() => togglePanel("panel6")}
						className={expanded === "panel6" ? "is-open" : ""}
					>
						<Box className="left-content">
							<PeopleAltTwoToneIcon className="parent-icon" />
							<span>Visitor Management</span>
						</Box>
						<ExpandMoreIcon className="expand-chevron" />
					</ParentFolderButton>
					<Collapse sx={{ ml: "20px" }} in={expanded === "panel6"} timeout="auto" unmountOnExit>
						<SubmenuTreeBox>
							<ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/visitor/ticket-config") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/ticket-config"
								>
									<span>Ticket Configuration</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/visitor/create-ticket") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/create-ticket"
								>
									<span>Create Ticket</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/visitor/ticket-list") ||
											currentPath.startsWith("/visitor/ticket/")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/ticket-list"
								>
									<span>Ticket List</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/visitor/list") ||
											currentPath.startsWith("/visitor/details")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/list"
								>
									<span>All Visitor List</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/visitor/scan-ticket") ||
											currentPath.startsWith("/visitor/ticket-scan")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/scan-ticket"
								>
									<span>Scan Ticket</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/visitor/scan-history") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/scan-history"
								>
									<span>Scan History</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/visitor/gift-management") ||
										currentPath.startsWith("/visitor/gift")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/gift-management"
								>
									<span>Gift Management</span>
								</NavSubItemButton>
							</ListItem>

							{/* <ListItem>
								<Button
									className="submodule-name"
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/visitor-invoice"
									startIcon={
										<TableChartTwoToneIcon />
									}
								>
									All Visitor Invoice
								</Button>
							</ListItem>
							<ListItem>
								<Button
									className="submodule-name"
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/create-invoice"
									startIcon={
										<TableChartTwoToneIcon />
									}
								>
									Create Visitor Invoice
								</Button>
							</ListItem>
							<ListItem>
								<Button
									className="submodule-name"
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/visitor/money-receipt"
									startIcon={
										<TableChartTwoToneIcon />
									}
								>
									Money Receipt
								</Button>
							</ListItem> */}
						</SubmenuTreeBox>
					</Collapse>
				</Box>

				{/* Fair List */}
				<ListItem component="div">
					<GroupChildButton
						disableRipple
						component={RouterLink}
						onClick={closeSidebar}
						to="/fair/list"
						className={
							(currentPath === "/fair/list" ||
								currentPath === "/fair" ||
								currentPath.startsWith("/fair/list/") ||
								currentPath.startsWith("/fair/stall")) &&
							!currentPath.startsWith("/fair/feedback")
								? "active"
								: ""
						}
					>
						<FestivalTwoToneIcon className="parent-icon" />
						<span>Fair List</span>
					</GroupChildButton>
				</ListItem>

				{/* Conference & Whatson (Preserved Commented Section) */}
				{/* <Accordion
					className="accordion"
					expanded={expanded === "panel15"}
					onChange={handleChange("panel15")}
				>
					<AccordionSummary
						expandIcon={
							<ExpandMoreIcon className="expand-icon" />
						}
						aria-controls="panel2bh-content"
						id="panel2bh-header"
					>
						<Typography className="accordion-module-name">
							Conference & Whatson
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ padding: 0 }}>
						<List component="div">
							<ListItem component="div">
								<Button
									sx={{
										paddingLeft:
											"18px!important",
									}}
									disableRipple
									component={RouterLink}
									className="submodule-name"
									onClick={closeSidebar}
									to="/conference&whatson/conference_list"
									startIcon={
										<TableChartTwoToneIcon />
									}
								>
									Conference List
								</Button>
							</ListItem>
							<ListItem component="div">
								<Button
									sx={{
										paddingLeft:
											"18px!important",
									}}
									disableRipple
									component={RouterLink}
									className="submodule-name"
									onClick={closeSidebar}
									to="/conference&whatson/whatson_list"
									startIcon={
										<TableChartTwoToneIcon />
									}
								>
									Whatson List
								</Button>
							</ListItem>
						</List>
					</AccordionDetails>
				</Accordion> */}

				{/* Fair Event Parent */}
				{/* <Box>
					<ParentFolderButton
						disableRipple
						onClick={() => togglePanel("panel10")}
						className={expanded === "panel10" ? "is-open" : ""}
					>
						<Box className="left-content">
							<EventNoteTwoToneIcon className="parent-icon" />
							<span>Event</span>
						</Box>
						<ExpandMoreIcon className="expand-chevron" />
					</ParentFolderButton>
					<Collapse sx={{ ml: "20px" }} in={expanded === "panel10"} timeout="auto" unmountOnExit>
						<SubmenuTreeBox>
							<ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/fair-event/create-event") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/fair-event/create-event"
								>
									<span>Create Event</span>
								</NavSubItemButton>
							</ListItem>
							<ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/fair-event/all-event-list") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/fair-event/all-event-list"
								>
									<span>All Events</span>
								</NavSubItemButton>
							</ListItem>
						</SubmenuTreeBox>
					</Collapse>
				</Box> */}

				{/* B2B Session Parent */}
				<Box>
					<ParentFolderButton
						disableRipple
						onClick={() => togglePanel("panel-b2b-session")}
						className={expanded === "panel-b2b-session" ? "is-open" : ""}
					>
						<Box className="left-content">
							<GroupsTwoToneIcon className="parent-icon" />
							<span>B2B Management</span>
						</Box>
						<ExpandMoreIcon className="expand-chevron" />
					</ParentFolderButton>
					<Collapse sx={{ ml: "20px" }} in={expanded === "panel-b2b-session"} timeout="auto" unmountOnExit>
						<SubmenuTreeBox>
							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath === "/b2b/list" ||
											currentPath.startsWith("/b2b/list")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/b2b/list"
								>
									<span>B2B Member List</span>
								</NavSubItemButton>
							</ListItem>
							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath === "/b2b-session/list" ||
											currentPath === "/b2b-session"
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/b2b-session/list"
								>
									<span>Session List</span>
								</NavSubItemButton>
							</ListItem>
							{/* <ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/b2b-session/requests") ||
											currentPath.startsWith("/b2b-session/request-list")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/b2b-session/requests"
								>
									<span>Session Requests</span>
								</NavSubItemButton>
							</ListItem> */}
							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/b2b-session/enroll")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/b2b-session/enroll"
								>
									<span>Session Enrollments For B2B</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/b2b/b2b-invoice") ||
											currentPath.startsWith("/b2b/create-b2b-invoice")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/b2b/b2b-invoice"
								>
									<span>B2B Invoices</span>
								</NavSubItemButton>
							</ListItem>
							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/b2b/money-receipt")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/b2b/money-receipt"
								>
									<span>Money Receipt</span>
								</NavSubItemButton>
							</ListItem>
						</SubmenuTreeBox>
					</Collapse>
				</Box>

				{/* Feedback & Suggestions */}
				<ListItem component="div">
					<GroupChildButton
						disableRipple
						component={RouterLink}
						onClick={closeSidebar}
						to="/fair/feedback"
						className={
							currentPath.startsWith("/fair/feedback") ||
								currentPath.startsWith("/feedback")
								? "active"
								: ""
						}
					>
						<RateReviewTwoToneIcon className="parent-icon" />
						<span>Feedback &amp; Suggestions</span>
					</GroupChildButton>
				</ListItem>

				{/* Entry Fair (Preserved Commented Section) */}
				{/* <Accordion
					className="accordion"
					expanded={expanded === "panel11"}
					onChange={handleChange("panel11")}
				>
					<AccordionSummary
						expandIcon={
							<ExpandMoreIcon className="expand-icon" />
						}
						aria-controls="panel1bh-content"
						id="panel1bh-header"
					>
						<Typography className="accordion-module-name">
							Entry fair
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ padding: 0 }}>
						<ListItem>
							<Button
								className="submodule-name"
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to="/entry-fair/list"
								startIcon={
									<TableChartTwoToneIcon />
								}
							>
								Entry fair list
							</Button>
						</ListItem>
					</AccordionDetails>
				</Accordion> */}

				{/* Raffle Draw Parent */}
				<Box>
					<ParentFolderButton
						disableRipple
						onClick={() => togglePanel("panel11")}
						className={expanded === "panel11" ? "is-open" : ""}
					>
						<Box className="left-content">
							<CardGiftcardTwoToneIcon className="parent-icon" />
							<span>Raffle Draw</span>
						</Box>
						<ExpandMoreIcon className="expand-chevron" />
					</ParentFolderButton>
					<Collapse sx={{ ml: "20px" }} in={expanded === "panel11"} timeout="auto" unmountOnExit>
						<SubmenuTreeBox>
							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/raffle-draw/prize")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/raffle-draw/prize"
								>
									<span>Prize List</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/raffle-draw/participant")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/raffle-draw/participant"
								>
									<span>Participant List</span>
								</NavSubItemButton>
							</ListItem>

							<ListItem component="div">
								<NavSubItemButton
									className={
										currentPath.startsWith("/raffle-draw/winner")
											? "active"
											: ""
									}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/raffle-draw/winner"
								>
									<span>Winner List</span>
								</NavSubItemButton>
							</ListItem>
						</SubmenuTreeBox>
					</Collapse>
				</Box>

				{/* Guest & Speaker (Preserved Commented Section) */}
				{/* <Accordion
					className="accordion"
					expanded={expanded === "panel27"}
					onChange={handleChange("panel27")}
				>
					<AccordionSummary
						expandIcon={
							<ExpandMoreIcon className="expand-icon" />
						}
						aria-controls="panel2bh-content"
						id="panel2bh-header"
					>
						<Typography className="accordion-module-name">
							Guest & Speaker
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ padding: 0 }}>
						<List component="div">
							<ListItem component="div">
								<Button
									sx={{
										paddingLeft:
											"18px!important",
									}}
									disableRipple
									component={RouterLink}
									className="submodule-name"
									onClick={closeSidebar}
									to="/guest-speaker/guest-list"
									startIcon={
										<TableChartTwoToneIcon />
									}
								>
									Guest & Speaker List
								</Button>
							</ListItem>
						</List>
					</AccordionDetails>
				</Accordion> */}
			</List>

			{/* SUPPORT (Preserved Commented Section) */}
			{/* <List
				component='div'
				subheader={
					<ListSubheader component='div' disableSticky>
						SUPPORT
					</ListSubheader>
				}
			>
				<SubMenuWrapper>
					<List component='div'>
						<ListItem component='div'>
							<Button
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to='/support/conversations'
								startIcon={<TableChartTwoToneIcon />}
							>
								Conversations
							</Button>
						</ListItem>
					</List>
				</SubMenuWrapper>
			</List> */}

			{/* SMS PANEL (Preserved Commented Section) */}
			{/* <List
				component='div'
				subheader={
					<ListSubheader component='div' disableSticky>
						SMS PANEL
					</ListSubheader>
				}
			>
				<SubMenuWrapper>
					<List component='div'>
						<ListItem component='div'>
							<Button
								disableRipple
								component={RouterLink}
								onClick={closeSidebar}
								to='/sms'
								startIcon={<TableChartTwoToneIcon />}
							>
								SMS
							</Button>
						</ListItem>
					</List>
				</SubMenuWrapper>
			</List> */}
			{/* ACCOUNTS SECTION */}
			<List
				component="div"
				subheader={
					<GroupHeader component="div" disableSticky>
						<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 15, color: "#F59E0B" }} />
						<span>Accounts &amp; Finance</span>
					</GroupHeader>
				}
			>
				<SubmenuTreeBox>
					<ListItem component="div">
						<NavSubItemButton
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/accounts/list"
							className={currentPath.startsWith("/accounts") ? "active" : ""}
						>
							<span>Account List</span>
						</NavSubItemButton>
					</ListItem>
				</SubmenuTreeBox>
			</List>
			{/* CONFIGURATIONS */}
			<List
				component="div"
				subheader={
					<GroupHeader component="div" disableSticky>
						<TuneOutlinedIcon sx={{ fontSize: 14, color: "#2DD4BF" }} />
						<span>Configurations</span>
					</GroupHeader>
				}
			>
				<SubmenuTreeBox>
					<ListItem component="div">
						<NavSubItemButton
							className={
								currentPath === "/service-item/list" ||
									currentPath === "/service-item"
									? "active"
									: ""
							}
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/service-item/list"
						>
							<span>Service Items</span>
						</NavSubItemButton>
					</ListItem>
					<ListItem component="div">
						<NavSubItemButton
							className={
								currentPath.startsWith("/service-item/config") ||
									currentPath.startsWith("/service-item/payment-config") ||
									currentPath.startsWith("/payment-item-config")
									? "active"
									: ""
							}
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/service-item/config"
						>
							<span>Payment Item Config</span>
						</NavSubItemButton>
					</ListItem>
					{/* <ListItem component="div">
						<NavSubItemButton
							className={currentPath.startsWith("/product-categories") ? "active" : ""}
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/product-categories/list"
						>
							<span>Product Categories</span>
						</NavSubItemButton>
					</ListItem> */}
					<ListItem component="div">
						<NavSubItemButton
							className={
								currentPath.startsWith("/chatbot/knowledge-base") ||
									currentPath.startsWith("/chatbot-knowledge-base") ||
									currentPath.startsWith("/configurations/chatbot-knowledge-base")
									? "active"
									: ""
							}
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/chatbot/knowledge-base"
						>
							<span>Chatbot Knowledge Base</span>
						</NavSubItemButton>
					</ListItem>
				</SubmenuTreeBox>
			</List>

			{/* REPORTS */}
			<List
				component="div"
				subheader={
					<GroupHeader component="div" disableSticky>

						<ReportIcon sx={{ fontSize: 15, color: "#FB7185" }} />
						<span>Reports &amp; Analytics</span>
					</GroupHeader>
				}
			>

				<Box>
					{/* <ParentFolderButton
						disableRipple
						onClick={() => togglePanel("report-panel1")}
						className={expanded === "report-panel1" ? "is-open" : ""}
					>
						<Box className="left-content">
							<AssessmentTwoToneIcon className="parent-icon" />
							<span>Report List</span>
						</Box>
						<ExpandMoreIcon className="expand-chevron" />
					</ParentFolderButton> */}
					<Collapse in={expanded === "report-panel1" || true} timeout="auto">
						<SubmenuTreeBox>
							{/* <ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/report/account-report") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/report/account-report"
								>
									<span>Account Report</span>
								</NavSubItemButton>
							</ListItem> */}
							{/* <ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/report/exhibitor-report") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/report/exhibitor-report/list"
								>
									<span>Exhibitor Ledger</span>
								</NavSubItemButton>
							</ListItem> */}
							<ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/report/b2b") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/report/b2b/list"
								>
									<span>B2B Ledger</span>
								</NavSubItemButton>
							</ListItem>
							{/* <ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/report/visitor") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/report/visitor/list"
								>
									<span>Visitor Ledger</span>
								</NavSubItemButton>
							</ListItem> */}
							{/* <ListItem component="div">
								<NavSubItemButton
									className={currentPath.startsWith("/report/collection") ? "active" : ""}
									disableRipple
									component={RouterLink}
									onClick={closeSidebar}
									to="/report/collection/list"
								>
									<span>Collection Report</span>
								</NavSubItemButton>
							</ListItem> */}
						</SubmenuTreeBox>
					</Collapse>
				</Box>
			</List>

			{/* PROFILE */}
			<List
				component="div"
				subheader={
					<GroupHeader component="div" disableSticky>
						<PersonOutlineOutlinedIcon sx={{ fontSize: 14, color: "#60A5FA" }} />
						<span>Profile</span>
					</GroupHeader>
				}
			>
				<SubmenuTreeBox>
					<ListItem component="div">
						<NavSubItemButton
							disableRipple
							component={RouterLink}
							onClick={closeSidebar}
							to="/profile"
							className={currentPath.startsWith("/profile") ? "active" : ""}
						>
							<span>User Profile</span>
						</NavSubItemButton>
					</ListItem>
				</SubmenuTreeBox>
			</List>
		</SidebarNavContainer>
	);
}

export default SidebarMenu;
